// @ts-nocheck
// src/pages/WaitingList.jsx
import React, { useState, useEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import { useTranslation } from "react-i18next";
import GenderIcon from "../components/GenderIcon";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import HasSiblings from "../utils/HasSiblings";
import GetFamilyName from "../utils/GetFamilyName";
import GetSchoolYear from "../utils/GetSchoolYear";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import { placeChildFromWaitingList } from "../utils/assignChildToClassroom";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { calculateAge, parseToDate } from "../utils/dateUtils";
import { calculatePriorityScore } from "../utils/calculatePriorityScore";
import { translateClassName } from "../utils/classroomUtils";
import { FaHourglassHalf, FaLock, FaSearch } from "react-icons/fa";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";
import {
  PlacementHero,
  PlacementPageShell,
  PlacementSection,
} from "./planning/components/PlacementLds";

import { FaChevronDown, FaListOl, FaUserPlus } from "react-icons/fa6";

import { useBackNavigation } from "../components/navigation/BackButton";
const InlineSpinner = () => (
  <span
    aria-hidden="true"
    className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/70 border-t-transparent"
  />
);

function getCurrentSchoolYear(schoolYears) {
  const today = new Date();
  return schoolYears.find((year) => {
    const start = parseToDate(year?.startDate);
    const end = parseToDate(year?.endDate);
    return start && end && today >= start && today <= end;
  });
}

const WaitingList: React.FC = () => {
  // Alleen nog nodig om token door te geven aan bestaande helper-functies
  const { keycloak } = useContext(AuthContext);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetchedOnceRef = useRef(false);

  // ✅ Backend-permissions
  const canViewWaitingList = useCan("school.waiting-list", "view");
  const canViewChildDetails = useCan("family.children", "read");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allChildren, setAllChildren] = useState([]);
  const [children, setChildren] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [families, setFamilies] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [assignedChildren, setAssignedChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [classroomSearchTerm, setClassroomSearchTerm] = useState("");
  const [modalSchoolYearId, setModalSchoolYearId] = useState("");
  const [modalAssignError, setModalAssignError] = useState("");
  const [assigningChildIds, setAssigningChildIds] = useState(() => new Set());
  const assigningChildIdsRef = useRef(new Set());
  const [assignmentErrorsByChild, setAssignmentErrorsByChild] = useState({});
  const [showDropdown, setShowDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  const getChildAssignmentKey = (childId) => String(childId);
  const isChildAssigning = (childId) =>
    assigningChildIds.has(getChildAssignmentKey(childId));

  const startChildAssignment = (childId) => {
    const key = getChildAssignmentKey(childId);
    if (assigningChildIdsRef.current.has(key)) return false;

    assigningChildIdsRef.current.add(key);
    setAssigningChildIds(new Set(assigningChildIdsRef.current));
    setAssignmentErrorsByChild((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    return true;
  };

  const finishChildAssignment = (childId) => {
    const key = getChildAssignmentKey(childId);
    assigningChildIdsRef.current.delete(key);
    setAssigningChildIds(new Set(assigningChildIdsRef.current));
  };

  const handleAssignmentError = (childId, err, fallbackKey) => {
    console.error(
      "❌ Assign + unset waiting faalde:",
      err?.response?.data || err?.message,
    );
    const mappedMessage =
      mapHttpErrorToMessage(err, t)?.message ||
      t(fallbackKey, {
        defaultValue: "Assignment failed. Please try again.",
      });
    setAssignmentErrorsByChild((prev) => ({
      ...prev,
      [getChildAssignmentKey(childId)]: mappedMessage,
    }));
    return mappedMessage;
  };

  // Back to the previous Lumen page, or home; never out of Lumen.
  const handleMobileBack = useBackNavigation("/");

  // siblingsByChild: { [childId]: string[] }
  const [siblingsByChild, setSiblingsByChild] = useState({});

  const formatDate = (value) => {
    const date = parseToDate(value);
    return date ? date.toLocaleDateString("nl-NL") : "—";
  };

  const getYear = (value) => {
    const date = parseToDate(value);
    return date ? date.getFullYear() : null;
  };

  const getDateKey = (value) => {
    const date = parseToDate(value);
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getEntitySchoolYearId = (entity) =>
    entity?.schoolYearId ??
    entity?.school_year_id ??
    entity?.schoolYear?.id ??
    entity?.schoolYear?.schoolYearId ??
    entity?.schoolYear?.school_year_id ??
    entity?.id ??
    null;

  const getSchoolYearSelectValue = (schoolYear) => {
    const id = getEntitySchoolYearId(schoolYear);
    return id != null ? String(id) : getSchoolYearLabel(schoolYear);
  };

  const classroomBelongsToSchoolYear = (classroom, schoolYear) => {
    if (!classroom || !schoolYear) return false;

    const classroomSchoolYearId =
      classroom.schoolYearId ??
      classroom.school_year_id ??
      classroom.schoolYear?.id ??
      classroom.schoolYear?.schoolYearId ??
      classroom.schoolYear?.school_year_id ??
      null;
    const schoolYearId = getEntitySchoolYearId(schoolYear);

    if (classroomSchoolYearId != null && schoolYearId != null) {
      return String(classroomSchoolYearId) === String(schoolYearId);
    }

    return (
      getDateKey(classroom.startDate) === getDateKey(schoolYear.startDate) &&
      getDateKey(classroom.endDate) === getDateKey(schoolYear.endDate)
    );
  };

  const getSchoolYearLabel = (schoolYear) => {
    if (!schoolYear) return "—";
    if (schoolYear.label) return schoolYear.label;

    const startYear = getYear(schoolYear.startDate);
    const endYear = getYear(schoolYear.endDate);
    return startYear && endYear ? `${startYear}/${endYear}` : "—";
  };

  const modalSchoolYearOptions = schoolYears.map((year) => ({
    ...year,
    id: getSchoolYearSelectValue(year),
    label: getSchoolYearLabel(year),
  }));

  const getClassTypeLabel = (classroom) => {
    const classType = classroom?.classType || classroom?.level;
    if (classType === "KINDERGARTEN") return "Kleuterklas";
    if (classType === "GRADE") return "Klas";
    return classType || "";
  };

  const formatClassroomName = (classroom) => {
    const translated = translateClassName(classroom);
    return translated || classroom?.className || "—";
  };

  const getExpectedSchoolYearForChild = (child) => {
    if (!child) return null;

    const expected = GetSchoolYear(child.dateBirth, classrooms, schoolYears);
    if (expected?.schoolYear) return expected.schoolYear;

    const expectedSchoolYearId = expected?.schoolYearId;
    if (expectedSchoolYearId != null) {
      const matchById = schoolYears.find(
        (year) =>
          String(getEntitySchoolYearId(year)) === String(expectedSchoolYearId),
      );
      if (matchById) return matchById;
    }

    const expectedLabel = expected?.projectedYear || expected?.label;
    if (expectedLabel) {
      return (
        schoolYears.find(
          (year) => getSchoolYearLabel(year) === expectedLabel,
        ) || null
      );
    }

    return null;
  };

  useEffect(() => {
    // 1) Wacht tot de permissie *bepaald* is (kan eerst undefined zijn)
    if (canViewWaitingList === undefined) return;

    // 2) Geen recht → éénmalig 403 tonen, daarna klaar
    if (canViewWaitingList === false) {
      if (!fetchedOnceRef.current) {
        setError({ message: t("error.403.message"), status: 403 });
        setLoading(false);
        fetchedOnceRef.current = true;
      }
      return;
    }

    // 3) Al een keer opgehaald? Niet nog eens
    if (fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [childrenRes, classroomsRes, familiesRes, yearsRes] =
          await Promise.allSettled([
            http.get(`/children`),
            http.get(`/classrooms`),
            http.get(`/families`), // optioneel; kan 403 zijn
            http.get(`/years`),
          ]);
        if (cancelled) return;

        if (childrenRes.status === "fulfilled") {
          const all = Array.isArray(childrenRes.value.data)
            ? childrenRes.value.data
            : [];
          setAllChildren(all);
          const waitingChildren = all.filter((c) => c.waiting);
          setChildren(waitingChildren);
        } else {
          throw childrenRes.reason;
        }
        setClassrooms(
          classroomsRes.status === "fulfilled"
            ? classroomsRes.value.data || []
            : [],
        );
        setFamilies(
          familiesRes.status === "fulfilled"
            ? familiesRes.value.data || []
            : [],
        );
        setSchoolYears(
          yearsRes.status === "fulfilled" ? yearsRes.value.data || [] : [],
        );
      } catch (err) {
        if (!cancelled) setError(mapHttpErrorToMessage(err, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
    // let op: minimale wijziging — t uit deps weglaten om re-runs te voorkomen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewWaitingList]);

  // 🆕 Detecteer mobiele layout (ongeacht portrait/landscape)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLayout = () => {
      const width = window.innerWidth;
      // zelfde grens als Tailwind `lg` → alles daaronder beschouwen we als "mobiel"
      setIsMobileLayout(width < 1024);
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);
    window.addEventListener("orientationchange", updateLayout);

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("orientationchange", updateLayout);
    };
  }, []);

  // siblings map ophalen zodra er kinderen zijn
  useEffect(() => {
    const fetchSiblings = async () => {
      if (children.length === 0) return;

      try {
        const ids = children.map((c) => c.id);
        const { data } = await http.post(`/children/siblings`, ids);
        setSiblingsByChild(data || {});
      } catch {
        setSiblingsByChild({});
      }
    };

    fetchSiblings();
  }, [children]);

  useEffect(() => {
    if (!selectedChild) return;

    setClassroomSearchTerm("");
    setModalAssignError("");
    const expected = getExpectedSchoolYearForChild(selectedChild);
    const fallback = expected || getCurrentSchoolYear(schoolYears) || schoolYears[0] || null;
    setModalSchoolYearId(fallback ? getSchoolYearSelectValue(fallback) : "");
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild, schoolYears]);

  useEffect(() => {
    if (!showDropdown) return;
    const handler = (event) => {
      const element = event.target instanceof Element
        ? event.target
        : event.target?.parentElement;
      if (element?.closest(`[data-wl-dropdown="${showDropdown}"]`)) return;
      setShowDropdown(null);
      setDropdownPosition(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  const sortedChildren = [...children]
    .map((child) => ({
      ...child,
      priorityScore: calculatePriorityScore(
        child,
        allChildren,
        classrooms,
        siblingsByChild,
      ),
      priorityAppliedRules: Array.isArray(child.priorityAppliedRules)
        ? child.priorityAppliedRules
        : [],
    }))
    .sort((a, b) => b.priorityScore - a.priorityScore);

  const filteredChildren = sortedChildren.filter((child) => {
    const familyName = GetFamilyName(child.familyId, families) || "";
    const sy = GetSchoolYear(child.dateBirth, classrooms, schoolYears) || {};
    const label = sy.label || "";
    const className = (sy.className || "").toLowerCase();

    return (
      (child.firstName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (child.lastName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      familyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (label || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      className.includes(searchTerm.toLowerCase())
    );
  });

  const currentSchoolYear = getCurrentSchoolYear(schoolYears);
  const expectedSchoolYear = getExpectedSchoolYearForChild(selectedChild);
  const selectedModalSchoolYear =
    schoolYears.find((year) => getSchoolYearSelectValue(year) === modalSchoolYearId) ||
    expectedSchoolYear ||
    currentSchoolYear ||
    null;
  const filteredModalClassrooms = classrooms.filter((classroom) => {
    if (
      !selectedModalSchoolYear ||
      !classroomBelongsToSchoolYear(classroom, selectedModalSchoolYear)
    ) {
      return false;
    }

    const query = classroomSearchTerm.toLowerCase();
    if (!query) return true;

    return (
      (classroom.className || "").toLowerCase().includes(query) ||
      formatClassroomName(classroom).toLowerCase().includes(query) ||
      getClassTypeLabel(classroom).toLowerCase().includes(query)
    );
  });

  if (loading) return <Spinner />;
  if (error)
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <>
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-content-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > * {
          animation: lds-content-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > *:nth-child(2) {
          animation-delay: 45ms;
        }

        .lds-page-enter > *:nth-child(3) {
          animation-delay: 90ms;
        }

        .lds-page-enter > *:nth-child(4) {
          animation-delay: 135ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-page-enter > * {
            animation: none;
          }
        }
      `}</style>
      <div className="lds-page-enter">
    <PlacementPageShell contentClassName="max-w-7xl">
      <PlacementHero
        eyebrow={t("placement.overview.title", { defaultValue: "Placement" })}
        title={t("waiting-list.title", { defaultValue: "Interest list" })}
        description={t("waiting-list.description", {
          defaultValue:
            "Review priorities and place children in the appropriate classroom.",
        })}
        icon={<FaHourglassHalf aria-hidden="true" />}
        illustration={<FaHourglassHalf aria-hidden="true" />}
        badges={
          <span className="rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-500/40 dark:bg-slate-900/70 dark:text-indigo-200">
            {filteredChildren.length}{" "}
            {t("waiting-list.children", { defaultValue: "children" })}
          </span>
        }
      />
      {/* 🛑 Desktop-only overlay voor mobiele layout */}
      {isMobileLayout && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/85 px-6 text-center text-white">
          <p className="mb-2 text-lg font-semibold">
            {t("waiting-list.mobile_not_supported_title", {
              defaultValue: "Niet beschikbaar op mobiel",
            })}
          </p>
          <p className="max-w-xs text-sm text-gray-200 mb-4">
            {t("waiting-list.mobile_not_supported_message", {
              defaultValue:
                "De wachtlijstpagina is alleen beschikbaar op een laptop of desktop voor voldoende overzicht.",
            })}
          </p>

          <button
            type="button"
            onClick={handleMobileBack}
            className="mt-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-gray-900 shadow hover:bg-white dark:bg-gray-100 dark:text-gray-900"
          >
            {t("common.back", { defaultValue: "Ga terug" })}
          </button>
        </div>
      )}

      {!canViewChildDetails && (
        <div className="mb-3 text-sm text-gray-600 dark:text-gray-300">
          {t(
            "waiting-list.no_permission_child_details",
            "Je kunt geen kinddetails openen met jouw rechten.",
          )}
        </div>
      )}

      <PlacementSection className="p-4">
        <label className="relative block">
          <FaSearch
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            placeholder={t("waiting-list.search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-500/20"
          />
        </label>
      </PlacementSection>

      <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.10)] dark:border-slate-700 dark:bg-slate-900 dark:shadow-[0_20px_50px_rgba(0,0,0,0.40)]">
        <div className="max-w-full overflow-visible">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[4%]" />
              <col className="w-[13%]" />
              <col className="w-[9%]" />
              <col className="w-[11%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[8%]" />
              <col className="w-[10%]" />
              <col className="w-[11%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead className="bg-gradient-to-r from-indigo-50 via-slate-50 to-sky-50 text-slate-700 dark:from-indigo-950/70 dark:via-slate-900 dark:to-sky-950/60 dark:text-slate-200">
              <tr>
                <th className="border-b border-slate-200 px-2 py-3 text-center dark:border-slate-700">
                  <span className="sr-only">{t("waiting-list.gender")}</span>
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">
                  {t("waiting-list.child_name")}
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">{t("waiting-list.family")}</th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">
                  {t("waiting-list.birth_date")}
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">
                  {t("waiting-list.registration_date")}
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">{t("waiting-list.siblings")}</th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">{t("waiting-list.committed")}</th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">
                  {t("waiting-list.projected_year")}
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">
                  {t("waiting-list.suggested_class")}
                </th>
                <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide dark:border-slate-700">{t("common.actions.name")}</th>
              </tr>
            </thead>
            <tbody style={{ overflow: "visible" }}>
              {filteredChildren.length > 0 ? (
                filteredChildren.map((child) => {
                  const sy =
                    GetSchoolYear(child.dateBirth, classrooms, schoolYears) ||
                    {};
                  const classId = sy.classId || null;
                  const className = sy.className || "";
                  const projectedYear = sy.projectedYear || "";
                  const suggestedClassroom = classId
                    ? classrooms.find(
                        (classroom) => String(classroom.id) === String(classId),
                      )
                    : null;

                  const isAssigned = assignedChildren.includes(child.id);
                  const siblingIds = siblingsByChild[child.id] || [];
                  const hasSiblings = siblingIds.length > 0;
                  const appliedRuleSet = new Set(
                    child.priorityAppliedRules || [],
                  );
                  const age = calculateAge(child.dateBirth);
                  const ageLabel = Number.isFinite(age) ? age : "—";
                  const birthDateLabel = formatDate(child.dateBirth);
                  const registrationLabel = formatDate(child.dateRegistration);

                  const isDropdownOpen = showDropdown === child.id;
                  const isAssigning = isChildAssigning(child.id);
                  const rowClickable =
                    canViewChildDetails && !isAssigned && !isAssigning;
                  const assignmentError =
                    assignmentErrorsByChild[getChildAssignmentKey(child.id)];
                  const rowClass = rowClickable
                    ? "cursor-pointer transition hover:ring-2 hover:ring-indigo-200/70 dark:hover:ring-indigo-500/40"
                    : "cursor-default";

                  return (
                    <tr
                      key={child.id}
                      className={`relative border-b border-slate-100 bg-white transition hover:bg-indigo-50/45 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-indigo-950/20 ${
                        isAssigned
                          ? "line-through opacity-50 cursor-default"
                          : rowClass
                      } ${isDropdownOpen ? "z-30" : ""}`}
                      onClick={() => {
                        if (rowClickable) {
                          navigate(`/family/children/${child.id}/details`);
                        }
                      }}
                    >
                      <td className="p-2 text-center">
                        <GenderIcon gender={child.gender} />
                      </td>

                      {/* Naam: alleen klikbaar als je mag */}
                      <td className="p-3 dark:text-gray-300">
                        {rowClickable ? (
                          <button
                            className="text-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/family/children/${child.id}/details`);
                            }}
                            title={t("waiting-list.open_child_details")}
                          >
                            {child.firstName} {child.lastName}
                          </button>
                        ) : (
                          <span
                            className="inline-flex items-center gap-2 text-gray-800 dark:text-gray-200"
                            title={
                              !canViewChildDetails
                                ? t("waiting-list.no_permission_child_details")
                                : ""
                            }
                          >
                            {child.firstName} {child.lastName}
                            {!canViewChildDetails && (
                              <FaLock className="opacity-70" />
                            )}
                          </span>
                        )}
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        {child.families && child.families.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {child.families.map((family) => (
                              <span
                                key={family.id}
                                className="inline-block rounded-full bg-indigo-100 px-2 py-1 text-sm font-medium text-indigo-800 shadow dark:bg-indigo-900 dark:text-indigo-300"
                                title={family.familyName}
                              >
                                {family.familyName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span>
                            {t("children.details.no_family_assigned")}
                          </span>
                        )}
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        {birthDateLabel} (<b>{ageLabel}</b>)
                        {Number.isFinite(age) && age > 16 && (
                          <span className="ml-2 px-2 py-0.5 text-xs text-red-700 bg-red-100 rounded">
                            {t("waiting-list.labels.possibly_too_old")}
                          </span>
                        )}
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        {registrationLabel}
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        <span className="inline-flex items-center space-x-2">
                          <HasSiblings
                            hasSiblings={hasSiblings}
                            count={siblingIds.length}
                          />
                          <span className="relative inline-block group">
                            <span
                              className="text-sm text-gray-500 focus:outline-none"
                              aria-describedby={`priority-${child.id}`}
                              tabIndex={0}
                            >
                              <FaListOl className="inline text-indigo-500" />{" "}
                              {child.priorityScore}
                            </span>
                            <div
                              id={`priority-${child.id}`}
                              role="tooltip"
                              className="pointer-events-auto absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-3 hidden w-56 rounded-md border border-gray-300 bg-white p-3 text-xs text-gray-700 shadow-xl group-hover:block group-focus-within:block backdrop-blur dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                              style={{
                                minWidth: "14rem",
                                whiteSpace: "normal",
                              }}
                            >
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {t("waiting-list.priority.total", {
                                  defaultValue: "Prioriteit",
                                })}
                                : {child.priorityScore}
                              </p>
                              <ul className="mt-2 space-y-1">
                                {[
                                  {
                                    id: "siblings",
                                    delta: "+3",
                                    label: t("waiting-list.priority.siblings", {
                                      defaultValue:
                                        "Broers/zussen al toegelaten",
                                    }),
                                  },
                                  {
                                    id: "committed",
                                    delta: "+2",
                                    label: t(
                                      "waiting-list.priority.committed",
                                      { defaultValue: "Toezegging" },
                                    ),
                                  },
                                  {
                                    id: "age",
                                    delta: "+1",
                                    label: t("waiting-list.priority.age", {
                                      defaultValue: "Leeftijd ≥5",
                                    }),
                                  },
                                  {
                                    id: "registration",
                                    delta: "+1",
                                    label: t(
                                      "waiting-list.priority.registration",
                                      {
                                        defaultValue: "Inschrijving >6 maanden",
                                      },
                                    ),
                                  },
                                  {
                                    id: "too_young",
                                    delta: "-2",
                                    label: t(
                                      "waiting-list.priority.too_young",
                                      { defaultValue: "Te jong" },
                                    ),
                                  },
                                  {
                                    id: "double_status",
                                    delta: "-1",
                                    label: t(
                                      "waiting-list.priority.double_status",
                                      { defaultValue: "Dubbel-status" },
                                    ),
                                  },
                                ].map((rule) => (
                                  <li
                                    key={rule.id}
                                    className={`flex justify-between ${
                                      appliedRuleSet.has(rule.id)
                                        ? "font-semibold text-gray-900 dark:text-gray-100"
                                        : "text-gray-500 dark:text-gray-400"
                                    }`}
                                  >
                                    <span>{rule.label}</span>
                                    <span>{rule.delta}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </span>
                        </span>
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        <span className="inline-flex items-center space-x-1">
                          <CommittedValidate committed={child.committed} />
                          <WaitingValidate waiting={child.waiting} />
                        </span>
                      </td>

                      <td className="p-3 dark:text-gray-300">
                        {projectedYear || "—"}
                      </td>

                      <td
                        className={`p-3 ${
                          className === "Too young"
                            ? "text-red-600 font-semibold"
                            : "dark:text-gray-300"
                        }`}
                      >
                        {translateClassName(className) || "—"}
                        {projectedYear ? ` (${projectedYear})` : ""}
                      </td>

                      <td
                        className="p-3 dark:text-gray-300 relative z-10"
                        data-wl-dropdown={child.id}
                      >
                        {!isAssigned && (
                          <div className="relative flex w-full min-w-0 items-stretch overflow-visible text-left">
                            {/* Direct assign knop */}
                            <button
                              type="button"
                              className={`inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-l-full border px-3 py-1.5 text-sm font-semibold transition ${
                                classId
                                  ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                                  : "cursor-not-allowed border-slate-300 bg-slate-300 text-white dark:border-slate-700 dark:bg-slate-700"
                              }`}
                              disabled={!classId || isAssigning}
                              aria-busy={isAssigning}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!classId) return;
                                if (!startChildAssignment(child.id)) return;
                                setShowDropdown((prev) =>
                                  prev === child.id ? null : prev,
                                );

                                try {
                                  // 1) Assign and clear waiting in one backend transaction
                                  await placeChildFromWaitingList(
                                    null,
                                    child.id,
                                    classId,
                                    {
                                      setCurrent: Boolean(
                                        suggestedClassroom &&
                                        currentSchoolYear &&
                                        classroomBelongsToSchoolYear(
                                          suggestedClassroom,
                                          currentSchoolYear,
                                        ),
                                      ),
                                    },
                                    setChildren,
                                  );

                                  // 2) Lokale state bijwerken zodat de badge direct verdwijnt
                                  setChildren((prev) =>
                                    prev.map((c) =>
                                      c.id === child.id
                                        ? { ...c, waiting: false }
                                        : c,
                                    ),
                                  );
                                  setAllChildren((prev) =>
                                    prev.map((c) =>
                                      c.id === child.id
                                        ? { ...c, waiting: false }
                                        : c,
                                    ),
                                  );

                                  // 3) UI markering (doorgestreept)
                                  setAssignedChildren((prev) => [
                                    ...prev,
                                    child.id,
                                  ]);
                                } catch (err) {
                                  handleAssignmentError(
                                    child.id,
                                    err,
                                    "waiting-list.assign-error",
                                  );
                                } finally {
                                  finishChildAssignment(child.id);
                                }
                              }}
                            >
                              <span className="inline-flex min-w-0 items-center justify-center gap-2">
                                {isAssigning ? (
                                  <>
                                    <InlineSpinner />
                                    {t("waiting-list.assigning", {
                                      defaultValue: "Assigning...",
                                    })}
                                  </>
                                ) : (
                                  <>
                                    <FaUserPlus aria-hidden="true" className="shrink-0" />
                                    <span className="whitespace-normal break-words text-left">
                                    {t("waiting-list.assign", {
                                      className: className || "-",
                                    })}
                                    </span>
                                  </>
                                )}
                              </span>
                            </button>

                            {/* ▼ Dropdown toggle */}
                            <button
                              type="button"
                              className="flex items-center justify-center rounded-r-full border border-l-0 border-emerald-600 bg-emerald-600 px-3 text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                              disabled={isAssigning}
                              aria-disabled={isAssigning}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isAssigning) return;
                                const nextOpen = showDropdown !== child.id;
                                if (nextOpen) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY + 8,
                                    left: Math.max(8, rect.right + window.scrollX - 192),
                                  });
                                } else {
                                  setDropdownPosition(null);
                                }
                                setShowDropdown(nextOpen ? child.id : null);
                              }}
                            >
                              <FaChevronDown aria-hidden="true" />
                            </button>

                            {/* Dropdown inhoud */}
                            {showDropdown === child.id && dropdownPosition && typeof document !== "undefined" && createPortal(
                              <div
                                data-wl-dropdown={child.id}
                                className="absolute z-[9999] w-48 rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                              >
                                <button
                                  type="button"
                                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  disabled={isAssigning}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isAssigning) return;
                                    setShowDropdown(null);
                                    setDropdownPosition(null);
                                    setSelectedChild(child);
                                    setShowModal(true);
                                  }}
                                >
                                  {t("waiting-list.more-options")}
                                </button>
                              </div>,
                              document.body,
                            )}
                          </div>
                        )}
                        {assignmentError && (
                          <p
                            role="alert"
                            aria-live="polite"
                            className="mt-2 max-w-[260px] text-xs font-medium text-red-600 dark:text-red-300"
                          >
                            {assignmentError}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="p-3 text-gray-500 dark:text-gray-400 text-center"
                  >
                    {t("children.overview.none_available")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedChild && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 dark:text-gray-200 rounded-3xl shadow-[0_35px_70px_rgba(0,0,0,0.45)] border border-white/60 dark:border-gray-700 p-6 w-[36rem]">
            <h2 className="text-lg font-bold mb-4">
              {t("waiting-list.select-modal.select-class")}
            </h2>
            <div className="mb-3">
              <SchoolYearDropdown
                schoolYears={modalSchoolYearOptions}
                selectedSchoolYearId={modalSchoolYearId}
                onChange={(nextSchoolYearId) => {
                  setModalSchoolYearId(nextSchoolYearId);
                  setClassroomSearchTerm("");
                  setModalAssignError("");
                }}
                label={t("waiting-list.select-modal.school-year", {
                  defaultValue: "Schooljaar",
                })}
              />
            </div>
            <input
              type="text"
              placeholder={t("waiting-list.select-modal.search-placeholder")}
              value={classroomSearchTerm}
              onChange={(e) => setClassroomSearchTerm(e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
            />
            {modalAssignError && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                {modalAssignError}
              </div>
            )}
            <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-md p-2 mt-2">
              {filteredModalClassrooms.length > 0 ? (
                filteredModalClassrooms.map((c) => {
                  const startYear = getYear(c.startDate);
                  const endYear = getYear(c.endDate);
                  const schoolYear =
                    startYear && endYear ? `${startYear}/${endYear}` : "—";
                  const typeLabel = getClassTypeLabel(c);
                  const classNameLabel = formatClassroomName(c);
                  const selectedChildAssigning = isChildAssigning(
                    selectedChild.id,
                  );
                  return (
                    <div
                      key={c.id}
                      className="flex justify-between items-center px-4 py-2 dark:hover:bg-gray-600"
                    >
                      <span className="dark:text-gray-200">
                        {typeLabel && (
                          <span className="mr-2 inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                            {typeLabel}
                          </span>
                        )}
                        {classNameLabel} ({schoolYear})
                      </span>
                      <button
                        type="button"
                        className="inline-flex min-w-[8rem] items-center justify-center gap-2 rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-70"
                        disabled={selectedChildAssigning}
                        aria-busy={selectedChildAssigning}
                        onClick={async (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (!selectedChild) return;
                          if (!startChildAssignment(selectedChild.id)) return;

                          try {
                            setModalAssignError("");
                            const setCurrent = currentSchoolYear
                              ? classroomBelongsToSchoolYear(
                                  c,
                                  currentSchoolYear,
                                )
                              : false;

                            // 1) Assign and clear waiting in one backend transaction
                            await placeChildFromWaitingList(
                              null,
                              selectedChild.id,
                              c.id,
                              {
                                grade: c.grade ?? null,
                                level: (c.classType || c.level) ?? null,
                                setCurrent,
                              },
                              setChildren,
                            );

                            // 2) Lokale state zodat badge direct uit is
                            setChildren((prev) =>
                              prev.map((ch) =>
                                ch.id === selectedChild.id
                                  ? { ...ch, waiting: false }
                                  : ch,
                              ),
                            );
                            setAllChildren((prev) =>
                              prev.map((ch) =>
                                ch.id === selectedChild.id
                                  ? { ...ch, waiting: false }
                                  : ch,
                              ),
                            );

                            // 3) UI markering (doorgestreept)
                            setAssignedChildren((prev) => [
                              ...prev,
                              selectedChild.id,
                            ]);

                            // 4) Sluit modal
                            setShowModal(false);
                            setSelectedChild(null);
                          } catch (err) {
                            setModalAssignError(
                              handleAssignmentError(
                                selectedChild.id,
                                err,
                                "waiting-list.select-modal.assign-error",
                              ),
                            );
                          } finally {
                            finishChildAssignment(selectedChild.id);
                          }
                        }}
                      >
                        {selectedChildAssigning ? (
                          <>
                            <InlineSpinner />
                            {t("waiting-list.assigning", {
                              defaultValue: "Assigning...",
                            })}
                          </>
                        ) : (
                          t("waiting-list.select-modal.select")
                        )}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                  {t("waiting-list.select-modal.no-classrooms", {
                    defaultValue: "Geen klassen gevonden voor dit schooljaar.",
                  })}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-500 dark:bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                disabled={isChildAssigning(selectedChild.id)}
                onClick={() => {
                  setShowModal(false);
                  setSelectedChild(null);
                  setModalAssignError("");
                }}
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </PlacementPageShell>

      </div>
    </>
  );
};

export default WaitingList;
